import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { SupoDocument } from '../model/supo-document';
import { NGXLogger } from 'ngx-logger';

export class FileUtilValidationConfig {
  maxFileSize: number;
  allowedMimeTypes: string[];

  public constructor(init?: Partial<FileUtilValidationConfig>) {
    Object.assign(this, init);
  }
}
@Injectable({
  providedIn: 'root'
})
export class FileUtil {
  /*
  readonly ALLOWED_MIME_TYPES = [
    'application/pdf',
    'image/png',
    'image/jpg',
    'image/jpeg',
    'image/heic',
    'application/vnd.ms-excel',
    'application/msexcel',
    'application/x-msexcel',
    'application/x-ms-excel',
    'application/x-excel',
    'application/x-dos_ms_excel',
    'application/xls',
    'application/x-xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ];
  readonly ALLOWED_FILE_TYPES = 'pdf, png, jpg, heic, xls';
  */
  readonly ALLOWED_MIME_TYPES = ['application/pdf', 'image/png', 'image/jpg', 'image/jpeg', 'image/heic'];
  readonly MAX_FILE_SIZE = 26214400;

  validationConfigDefault: FileUtilValidationConfig = new FileUtilValidationConfig({maxFileSize: this.MAX_FILE_SIZE, allowedMimeTypes: this.ALLOWED_MIME_TYPES});

  constructor(private logger: NGXLogger) {}

  public observableOfFile(file: File): Observable<SupoDocument> {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    return new Observable(subscriber => {
      reader.onload = () => {
        this.logger.debug('fileReader.onload ' + file.name);
        subscriber.next({ id: null, name: file.name, mimeType: file.type, data: reader.result });
        subscriber.complete();
      };
      reader.onerror = error => subscriber.error(error);
    });
  }

  public validateFiles(files: File[], validationConfig?: FileUtilValidationConfig): Map<string, string[]> {
    const validationConf = {...this.validationConfigDefault, ...validationConfig};    
    const validationResult: Map<string, string[]> = new Map<string, string[]>();

    const maxSizeExceededFiles = files.filter(file => file.size > validationConf.maxFileSize);
    if (maxSizeExceededFiles?.length > 0) {
      validationResult.set('MAX_FILE_SIZE', [''+validationConf.maxFileSize]);
      validationResult.set(
        'MAX_SIZE_EXCEEDED_FILES',
        maxSizeExceededFiles.map(file => file.name)
      );
    } else {
      validationResult.set('MAX_SIZE_EXCEEDED_FILES', []);
    }

    const unallowedFiles = files.filter(file => !validationConf.allowedMimeTypes.includes(file.type));
    if (unallowedFiles?.length > 0) {
      validationResult.set('ALLOWED_MIME_TYPES', validationConf.allowedMimeTypes);
      validationResult.set(
        'UNALLOWED_MIME_TYPES_FILES',
        unallowedFiles.map(file => file.name)
      );
    } else {
      validationResult.set('UNALLOWED_MIME_TYPES_FILES', []);
    }

    return validationResult;
  }

  public getInvalidFiles(validationResult: Map<string, string[]>): string[] | undefined {
    return validationResult?.get('UNALLOWED_MIME_TYPES_FILES');
  }

  public getMaxSizeExceededFiles(validationResult: Map<string, string[]>): string[] | undefined {
    return validationResult?.get('MAX_SIZE_EXCEEDED_FILES');
  }

  public getMaxFileSizeAsMB(validationResult?: Map<string, string[]>): string {
    return this.sizeToString(this.getMaxFileSize(validationResult));
  }

  public getMaxFileSize(validationResult?: Map<string, string[]>): number {
    if ( !validationResult ) {
      return this.validationConfigDefault.maxFileSize;
    }
    return !!validationResult?.get('MAX_FILE_SIZE') ? parseInt(validationResult?.get('MAX_FILE_SIZE')[0]) : 0;
  }

  public getAllowedMimeTypes(validationResult?: Map<string, string[]>): string[] {
    return validationResult?.get('ALLOWED_MIME_TYPES') || this.validationConfigDefault.allowedMimeTypes;
  }

  public getAllowedFilesTypes(validationResult?: Map<string, string[]>): string[] {
    return this.getFileTypesFromMimeTypes(validationResult?.get('ALLOWED_MIME_TYPES') || this.validationConfigDefault.allowedMimeTypes);
  }

  public getFileTypesFromMimeTypes(mimeTypes: string[]): string[] {
    return !!mimeTypes ? mimeTypes.map(m => m.split('/').pop()) : [];
  }

  private sizeToString(size: number): string {
    if ( size > 1024 ) {
      size = size / 1024.0;
    }
    if ( size > 1024 ) {
      size = size / 1024.0;
      return size.toFixed(1) + 'MB';
    }

    return size.toFixed(0) + 'KB';
  }
}
